import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "form"];
  static values = {
    contentCssUrl: String,
    fontCssUrl: String,
  };
  static defaults = {
    contentCssUrl: "/static/tinymce/content.css",
    fontCssUrl: "/static/chinese_fonts.css",
  };

  // Dark mode is disabled
  getPrefersDark() {
    return false;
  }

  initialize() {
    this.handleContentChange = this.handleContentChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  connect() {
    this.initEditor();

    this.formTarget.addEventListener("submit", this.handleSubmit);
  }

  disconnect() {
    this.destroyEditor();
    this.formTarget.removeEventListener("submit", this.handleSubmit);
  }



  async initEditor() {
    const prefersDark = this.getPrefersDark();
    this.currentPrefersDark = prefersDark;

    if (!window.tinymce) {
      await import("tinymce/tinymce");
      await Promise.all([
        import("tinymce/icons/default/icons.min.js"),
        import("tinymce/themes/silver/theme.min.js"),
        import("tinymce/models/dom/model.min.js"),
        import("tinymce/plugins/importcss"),
        import("tinymce/plugins/autosave"),
        import("tinymce/plugins/fullscreen"),
        import("tinymce/plugins/image"),
        import("tinymce/plugins/link"),
        import("tinymce/plugins/table"),
        import("tinymce-i18n/langs/zh_CN"),
        import("tinymce/skins/ui/oxide/skin.css"),
      ]);
    }

    const tinymce = window.tinymce;

    const defaults = {
      language: "zh_CN",
      license_key: "gpl",
      plugins: "importcss autosave fullscreen image link table",
      menubar: false,
      statusbar: false,
      toolbar:
        "bold underline alignleft aligncenter | blocks fontfamily fontsize | table image | removeformat fullscreen",
      toolbar_sticky: true,
      images_upload_url: "/tinymce_assets",
      convert_urls: false,
      file_picker_types: "image media",
      skin: false, // Skin CSS is imported above, no need for TinyMCE to load it
      content_css: this.contentCssUrlOrDefault,
      font_css: this.fontCssUrlOrDefault,
      font_size_formats: "10pt 12pt 14pt 16pt 18pt 22pt 24pt 26pt 36pt 42pt",
      font_family_formats:
        "方正小标宋='方正小标宋简体',FZXiaoBiaoSong-B05; 方正仿宋='方正仿宋简体',FZFangSong-Z02; 方正楷体='方正楷体简体',FZKai-Z03; 方正黑体='方正黑体简体',FZHei-B01; Arial=arial,helvetica,sans-serif;Courier New=courier new,courier,monospace;Georgia=georgia,palatino;Tahoma=tahoma,arial,helvetica,sans-serif;Times New Roman=times new roman,times,serif;Verdana=verdana,geneva",
      block_formats:
        "一级标题=h1; 二级标题=h2; 三级标题=h3; 正文段落=p; 无格式=pre; 引用=blockquote;",
      importcss_append: true,
      formats: {
        h1: { block: "h1", attributes: { class: "my-h1" } },
        h2: { block: "h2", attributes: { class: "my-h2" } },
        h3: { block: "h3", attributes: { class: "my-h3" } },
        p: { block: "p", attributes: { class: "my-p" } },
      },
      style_formats: [
        { title: "标题1", format: "h1" },
        { title: "标题2", format: "h2" },
        { title: "标题3", format: "h3" },
        { title: "段落", format: "p" },
      ],
      setup: (editor) => {
        editor.on("change", this.handleContentChange);
      },
    };

    let config = Object.assign({ target: this.inputTarget }, defaults);
    tinymce.init(config);
  }

  get contentCssUrlOrDefault() {
    return this.hasContentCssUrlValue
      ? this.contentCssUrlValue
      : this.constructor.defaults.contentCssUrl;
  }

  get fontCssUrlOrDefault() {
    return this.hasFontCssUrlValue
      ? this.fontCssUrlValue
      : this.constructor.defaults.fontCssUrl;
  }

  destroyEditor() {
    const tinymce = window.tinymce;
    if (!tinymce) return;

    let editor = tinymce.get(this.inputTarget.id);
    if (editor) {
      editor.off("change", this.handleContentChange);
      tinymce.remove(editor);
    }
  }

  async handleSubmit(event) {
    const tinymce = window.tinymce;
    if (!tinymce) return;

    let content = tinymce.get(this.inputTarget.id).getContent();
    const helperId = this.inputTarget.dataset.helperId;
    const required = this.inputTarget.dataset.required;
    const helperElement = document.getElementById(helperId);
    if (required && content.trim().length === 0) {
      if (helperElement) {
        helperElement.classList.add("block");
      }
      event.preventDefault();
    }
  }

  handleContentChange() {
    const tinymce = window.tinymce;
    if (!tinymce) return;

    let content = tinymce.get(this.inputTarget.id).getContent();
    const helperId = this.inputTarget.dataset.helperId;
    const required = this.inputTarget.dataset.required;
    const helperElement = document.getElementById(helperId);

    if (required && content.trim().length === 0) {
      if (helperElement) {
        helperElement.classList.add("block");
      }
    } else {
      if (helperElement) {
        helperElement.classList.remove("block");
      }
    }
  }
}
